import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import {
  queryGeoCities,
  queryGeoCountries,
  queryGeoProvinces,
} from '@/services/geo/query'
import {
  queryJobCategoryList,
  queryJobCompanyList,
  queryJobSkillList,
  queryJobTagList,
} from '@/services/job/query'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { FancyMultiSelect } from '@/components/ui/fancy-multi-select'
import { FormField } from '@/components/ui/form'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { FormFieldSelectCombobox } from '@/components/ui/form-field/select-combobox'
import { Input } from '@/components/ui/input'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap'

import { cn } from '@/lib/cn'
import { FormDto } from './helper'
import SchemaField from './schema-field'

export function JobForm() {
  const { control } = useFormContext<FormDto>()

  return (
    <Card className="max-w-full">
      <CardContent className="relative grid grid-cols-2 gap-x-2 gap-y-4 p-4 md:grid-cols-6">
        <FormField
          control={control}
          name="title"
          render={({ field }) => (
            <FormFieldset label="Nama Pekerjaan" className="col-span-3">
              <Input placeholder="Masukkan nama pekerjaan" {...field} />
            </FormFieldset>
          )}
        />
        <CompanyField />
        <CategoryField />
        <TagField />
        <FormField
          control={control}
          name="min"
          render={({ field }) => {
            return (
              <FormFieldset label="Minimum Salary" className="col-span-3">
                <Input placeholder="Masukkan min salary" {...field} />
              </FormFieldset>
            )
          }}
        />
        <FormField
          control={control}
          name="max"
          render={({ field }) => {
            return (
              <FormFieldset label="Maximum Salary" className="col-span-3">
                <Input placeholder="Masukkan max salary" {...field} />
              </FormFieldset>
            )
          }}
        />
        <LocationSection />
        <SchemaField />
        <DescriptionField />
        <SkillField />
        <IsActiveField />
      </CardContent>
      <CardFooter className="pt-4">
        <Button size="sm" className="ml-auto" type="submit">
          Simpan Job Listing
        </Button>
      </CardFooter>
    </Card>
  )
}

function CategoryField() {
  const { control } = useFormContext<FormDto>()
  const { isLoading, data } = useQuery(queryJobCategoryList())
  const items = (data ?? []).map(({ id, name }) => ({
    label: name,
    value: String(id),
  }))

  return (
    <FormField
      control={control}
      name="category_ids"
      render={({ field }) => {
        return (
          <FormFieldset label="Kategori" noFormControl className="col-span-3">
            <FormFieldSelect
              placeholder="Pilih kategori"
              disabled={isLoading}
              items={items}
              value={String(field.value || '')}
              onValueChange={field.onChange}
            />
          </FormFieldset>
        )
      }}
    />
  )
}

function TagField() {
  const { control } = useFormContext<FormDto>()
  const { isLoading, data } = useQuery(queryJobTagList())

  const items = (data ?? []).map(({ id, name }) => ({
    label: name,
    value: String(id),
  }))

  return (
    <FormField
      control={control}
      name="tag_ids"
      render={({ field }) => {
        return (
          <FormFieldset label="Tag" className="col-span-3" noFormControl>
            <FormFieldSelect
              placeholder="Pilih tag"
              disabled={isLoading}
              items={items}
              value={String(field.value || '')}
              onValueChange={field.onChange}
            />
          </FormFieldset>
        )
      }}
    />
  )
}

function CompanyField() {
  const { control } = useFormContext<FormDto>()
  const { isLoading, data } = useQuery(queryJobCompanyList())

  const items = (data ?? []).map(({ id, name }) => ({
    label: name,
    value: String(id),
  }))

  return (
    <FormField
      control={control}
      name="job_company_id"
      render={({ field }) => {
        return (
          <FormFieldset label="Perusahaan" className="col-span-3" noFormControl>
            <FormFieldSelect
              placeholder="Pilih perusahaan"
              disabled={isLoading}
              items={items}
              value={String(field.value || '')}
              onValueChange={field.onChange}
            />
          </FormFieldset>
        )
      }}
    />
  )
}

function LocationSection() {
  const { control, setValue } = useFormContext<FormDto>()
  const location = useWatch({ control, name: 'location' })

  const [sCountry, setSCountry] = useState(location.country?.name || '')
  const [sProvince, setSProvince] = useState(location.province.name || '')
  const [sCity, setSCity] = useState(location.city.name || '')

  const [countryDebounced] = useDebounce(sCountry, 500, { maxWait: 1000 })
  const [provinceDebounced] = useDebounce(sProvince, 500, { maxWait: 1000 })
  const [cityDebounced] = useDebounce(sCity, 500, { maxWait: 1000 })

  const countryId = location.country?.id
  const provinceId = location.province.id

  const queryCountries = useQuery(queryGeoCountries({ name: countryDebounced }))

  const queryProvinces = useQuery(
    queryGeoProvinces({
      country_id: countryId || '',
      name: provinceDebounced,
    })
  )
  const queryCities = useQuery(
    queryGeoCities({
      country_id: countryId || '',
      province_id: provinceId || '',
      name: cityDebounced,
    })
  )

  return (
    <>
      <FormField
        control={control}
        name="location.country.id"
        render={({ field }) => (
          <FormFieldset
            noFormControl
            label="Negara"
            className="col-span-full lg:col-span-2"
          >
            <FormFieldSelectCombobox
              placeholder="Pilih negara"
              items={queryCountries.data || []}
              value={field.value}
              disabled={queryCountries.isLoading}
              onValueChange={(val) => {
                const value = queryCountries.data?.find((x) => x.value === val)
                if (!value) return
                const { value: id, label: name } = value
                setValue('location.city', { id: '', name: '' })
                setValue('location.province', { id: '', name: '' })
                setValue('location.country', { id, name })
              }}
              search={sCountry}
              setSearch={setSCountry}
              isLoading={queryCountries.isFetching}
            />
          </FormFieldset>
        )}
      />

      <FormField
        control={control}
        name="location.province.id"
        render={({ field }) => (
          <FormFieldset
            noFormControl
            label="Provinsi / State / Perfecture"
            className="col-span-full md:col-span-3 lg:col-span-2"
          >
            <FormFieldSelectCombobox
              placeholder="Pilih provinsi"
              items={queryProvinces.data || []}
              value={field.value}
              disabled={!countryId}
              onValueChange={(val) => {
                const value = queryProvinces.data?.find((x) => x.value === val)
                if (!value) return
                const { value: id, label: name } = value
                setValue('location.city', { id: '', name: '' })
                setValue('location.province', { id, name })
              }}
              search={sProvince}
              setSearch={setSProvince}
              isLoading={queryProvinces.isFetching}
            />
          </FormFieldset>
        )}
      />
      <FormField
        control={control}
        name="location.city"
        render={({ field }) => (
          <FormFieldset
            label="City"
            className="col-span-full md:col-span-3 lg:col-span-2"
            noFormControl
          >
            <FormFieldSelectCombobox
              placeholder="Pilih kota"
              items={queryCities.data || []}
              value={field.value.id}
              disabled={!provinceId}
              onValueChange={(val) => {
                const value = queryCities.data?.find((x) => x.value === val)
                if (!value) return
                const { value: id, label: name } = value
                setValue('location.city', { id, name })
              }}
              search={sCity}
              setSearch={setSCity}
              isLoading={queryCities.isFetching}
            />
          </FormFieldset>
        )}
      />
    </>
  )
}

function SkillField() {
  const { control } = useFormContext<FormDto>()
  const { isLoading, data } = useQuery(queryJobSkillList())

  return (
    <FormField
      control={control}
      name="skill_ids"
      render={({ field }) => {
        return (
          <FormFieldset label="Skill" noFormControl className="col-span-full">
            <FancyMultiSelect
              disabled={isLoading}
              placeholder={field.value?.length ? '' : 'Select skill'}
              items={(data ?? []).map(({ id, name }) => ({
                label: name,
                value: String(id),
              }))}
              values={field.value?.map((x) => ({
                label: x.name,
                value: String(x.id),
              }))}
              onValuesChange={(values) => {
                field.onChange(
                  values.map((x) => ({ id: Number(x.value), name: x.label }))
                )
              }}
            />
          </FormFieldset>
        )
      }}
    />
  )
}

function DescriptionField() {
  const { control } = useFormContext<FormDto>()

  return (
    <FormField
      control={control}
      name="description"
      render={({ field }) => {
        return (
          <FormFieldset
            label="Deskripsi"
            className="col-span-full"
            noFormControl
          >
            <MinimalTiptapEditor
              throttleDelay={2000}
              className={cn('h-full min-h-56 w-full rounded-lg')}
              editorContentClassName="overflow-auto h-full"
              output="html"
              placeholder="Type your description here..."
              editable={true}
              editorClassName="focus:outline-none px-5 py-4 h-full"
              onChange={(value) => {
                field.onChange(value?.toString() || '')
              }}
              value={field.value}
            />
          </FormFieldset>
        )
      }}
    />
  )
}

function IsActiveField() {
  const { control } = useFormContext<FormDto>()

  return (
    <FormField
      control={control}
      name="is_active"
      render={({ field }) => {
        return (
          <FormFieldset label="Status Aktif" className="col-span-full">
            <FormFieldSelect
              placeholder="Pilih status"
              items={[
                { label: 'Aktif', value: 'true' },
                { label: 'Tidak Aktif', value: 'false' },
              ]}
              value={field.value ? 'true' : 'false'}
              onValueChange={(value) => field.onChange(value === 'true')}
            />
          </FormFieldset>
        )
      }}
    />
  )
}
